/**
 * inlineEdit — LUẬT THUẦN cho việc SỬA THẲNG TRÊN LƯỚI DANH SÁCH của DataListManager.
 *
 * Bù `col_editable` của InputBase, nhưng ở cấp TỪNG CỘT (`field.editableInList`) chứ không phải cả màn
 * hình: chỉ cột nào được cho phép mới sửa được.
 *
 * CÁCH LƯU (đã chốt): gom theo DÒNG. Sửa bao nhiêu ô tuỳ thích trong một dòng, rời dòng thì gửi MỘT lần
 * cho cả dòng. Ít request, và `checkBeforeSave` nhận được bản ghi đầy đủ để xét thay vì một bản ghi dở
 * dang (đổi số lượng nhưng chưa kịp đổi đơn giá).
 *
 * File KHÔNG import gì — `deepEqual` được TIÊM vào (dùng lại đúng hàm của `formRules.js`, không chép
 * lại) — để test node chạy được trên chính mã nguồn này.
 */

/**
 * Kiểu field sửa được ngay trên lưới. Cố ý KHÔNG có: ObjectArray (lưới lồng lưới), File/FileArray
 * (cần hộp thoại), RichText/Code/HTML (cần cả màn hình) — những thứ đó phải mở form.
 */
const INLINE_EDITABLE_TYPES = [
	'String', 'Number', 'Boolean', 'Date', 'DateTime', 'Time',
	'Select', 'Autocomplete', 'Radio', 'Slider',
];

/** Kiểu này có sửa được trên lưới không. Field LIÊN KẾT MODEL (`api`) thì luôn được, dù khai kiểu gì. */
function isTypeInlineEditable(field) {
	const f = field || {};
	if (f.api) return true;                       // ô chọn bản ghi (LookupFieldControl)
	return INLINE_EDITABLE_TYPES.indexOf(f.type || 'String') >= 0;
}

/**
 * Cột này có cho sửa trên lưới không.
 * @param field  khai báo cột
 * @param args   { context, formData (= DÒNG đang xét), masterData, fields } cho `editableInList` dạng script
 */
function resolveEditableInList(field, args) {
	const f = field || {};
	const raw = f.editableInList;
	// Chuỗi = script CHƯA được biên dịch (cấu hình nạp không qua parseDataListConfig). Đây là quyền GHI
	// nên chốt HAI LỚP: nhánh này, và `return raw === true` ở cuối (so sánh nghiêm ngặt, chuỗi không lọt).
	// Bỏ một trong hai vẫn an toàn; bỏ CẢ HAI thì `!!"return false"` là true và mọi cột khai script hoá
	// sửa được. Giữ nhánh này để ý định là tường minh, đừng gỡ khi thấy nó "thừa".
	if (typeof raw === 'string') return false;
	if (f.readOnly === true || f.isTemp === true) return false;
	if (!isTypeInlineEditable(f)) return false;
	if (typeof raw === 'function') {
		try { return !!raw(args && args.context, args && args.formData, args && args.masterData, args && args.fields); }
		catch (e) { return false; }
	}
	return raw === true;
}

/** Màn hình có đang ở trạng thái cho sửa thẳng trên lưới không (chưa xét từng cột). */
function canInlineEdit(opts) {
	const o = opts || {};
	if (o.canEdit !== true) return false;   // không có quyền sửa thì cột nào cũng chịu
	if (o.viewMode !== 'table') return false; // chỉ giao diện Bảng
	if (o.isMobile === true) return false;    // màn hẹp: ô nhập trong bảng cuộn ngang là cực hình
	return true;
}

/** Cột nào của cấu hình này có KHẢ NĂNG sửa trên lưới (chưa xét theo dòng). Dùng để quyết định bố cục. */
function hasInlineEditableField(fields) {
	return (fields || []).some(f => f && (f.editableInList === true || typeof f.editableInList === 'function')
		&& f.readOnly !== true && f.isTemp !== true && isTypeInlineEditable(f));
}

// =====================================================================
// BẢN NHÁP THEO DÒNG
// `drafts` là object phẳng { [rowId]: { field: value } } — chỉ chứa những ô NGƯỜI DÙNG đã gõ.
// Giữ riêng khỏi dữ liệu gốc để (1) hoàn nguyên được khi lưu lỗi và (2) biết chính xác đã đổi những gì.
// =====================================================================

/** Ghi một ô vào bản nháp. Trả về `drafts` MỚI (không sửa tại chỗ) để React nhận ra thay đổi. */
function setDraftValue(drafts, rowId, fieldName, value) {
	if (rowId === null || rowId === undefined || !fieldName) return drafts || {};
	const prev = (drafts || {})[rowId] || {};
	return { ...(drafts || {}), [rowId]: { ...prev, [fieldName]: value } };
}

/** Xoá bản nháp của một dòng (đã lưu xong, hoặc người dùng huỷ). Trả về `drafts` MỚI. */
function clearDraft(drafts, rowId) {
	if (!drafts || !Object.prototype.hasOwnProperty.call(drafts, rowId)) return drafts || {};
	const out = { ...drafts };
	delete out[rowId];
	return out;
}

/** Dòng để HIỂN THỊ: dữ liệu gốc phủ bản nháp lên trên. Không có nháp thì trả về ĐÚNG object gốc
 *  (giữ nguyên tham chiếu — đây là điều kiện để memo của dòng còn tác dụng). */
function draftedRow(row, drafts, rowId) {
	const d = (drafts || {})[rowId];
	if (!d || Object.keys(d).length === 0) return row;
	return { ...row, ...d };
}

/**
 * Những ô THẬT SỰ khác so với dữ liệu gốc. Người dùng bấm vào ô, gõ rồi gõ lại y như cũ, hoặc chỉ bấm
 * vào rồi bấm ra — không được coi là đổi, nếu không rời dòng nào cũng bắn một lệnh PUT vô nghĩa.
 * @param deepEqual hàm so sánh sâu (tiêm từ formRules.js)
 */
function pendingChanges(row, drafts, rowId, deepEqual) {
	const d = (drafts || {})[rowId];
	if (!d) return null;
	const eq = typeof deepEqual === 'function' ? deepEqual : ((a, b) => a === b);
	const out = {};
	let n = 0;
	for (const k of Object.keys(d)) {
		if (eq(d[k], (row || {})[k])) continue;
		out[k] = d[k];
		n++;
	}
	return n > 0 ? out : null;
}

/** Có dòng nào đang có thay đổi CHƯA LƯU không (để cảnh báo khi rời trang). */
function dirtyRowIds(rows, drafts, deepEqual, rowIdOf) {
	const ids = [];
	const idOf = typeof rowIdOf === 'function' ? rowIdOf : (r => r && (r._id || r.id));
	for (const r of (rows || [])) {
		const id = idOf(r);
		if (id === null || id === undefined) continue;
		if (pendingChanges(r, drafts, id, deepEqual)) ids.push(id);
	}
	return ids;
}

export {
	INLINE_EDITABLE_TYPES, isTypeInlineEditable, resolveEditableInList,
	canInlineEdit, hasInlineEditableField,
	setDraftValue, clearDraft, draftedRow, pendingChanges, dirtyRowIds,
};
